// URL-backed UI state for the hash-routed app ("#onboarding?tab=rejected&id=7").
// Keeping tab/modal state in the hash means a refresh restores it, and the
// browser Back button steps back through tabs/modals instead of jumping to
// the previous module.
import { useState, useEffect, useCallback } from "react";

const splitHash = () => {
  const raw = window.location.hash.replace(/^#/, "");
  const i = raw.indexOf("?");
  return i === -1 ? [raw, ""] : [raw.slice(0, i), raw.slice(i + 1)];
};

export const readHashParam = (key) => {
  const v = new URLSearchParams(splitHash()[1]).get(key);
  return v === null ? null : v;
};

const writeHashParam = (key, value, { push }) => {
  const [page, qs] = splitHash();
  const p = new URLSearchParams(qs);
  if (value === null || value === undefined || value === "") p.delete(key); else p.set(key, String(value));
  const next = p.toString();
  const url = window.location.pathname + window.location.search + "#" + page + (next ? "?" + next : "");
  if (url === window.location.pathname + window.location.search + window.location.hash) return;
  // pushState/replaceState don't fire "hashchange", so App's own hash
  // routing isn't disturbed; Back/Forward do fire it (and popstate).
  if (push) window.history.pushState({ hp: key }, "", url);
  else window.history.replaceState(window.history.state, "", url);
};

// [value, setValue, clear]. setValue pushes a history entry (so Back undoes
// it); setValue(v, { replace: true }) rewrites the current entry instead.
// clear() undoes the push made by this hook via Back when possible, so
// closing a modal with its X doesn't leave a duplicate history entry.
export const useHashParam = (key, fallback = null) => {
  const read = () => { const v = readHashParam(key); return v === null ? fallback : v; };
  const [value, setValue_] = useState(read);
  // The page this hook was mounted on. When the hash moves to another page (e.g. a notification opens
  // "#recruitment?id=53" while Employees is still mounted), that page's ?id= is not ours — ignore it,
  // otherwise Employees would try to open employee 53.
  const [ownPage] = useState(() => splitHash()[0]);

  useEffect(() => {
    const sync = () => { if (splitHash()[0] !== ownPage) return; setValue_(read()); };
    window.addEventListener("hashchange", sync);
    window.addEventListener("popstate", sync);
    return () => { window.removeEventListener("hashchange", sync); window.removeEventListener("popstate", sync); };
    // eslint-disable-next-line
  }, [key, ownPage]);

  const set = useCallback((v, opts = {}) => {
    if (splitHash()[0] !== ownPage) return; // the page has already moved on; don't write into another page's hash
    writeHashParam(key, v === fallback ? null : v, { push: !opts.replace });
    setValue_(v === null || v === undefined ? fallback : v);
  }, [key, fallback, ownPage]);

  const clear = useCallback(() => {
    if (splitHash()[0] !== ownPage) return;
    if (window.history.state?.hp === key && readHashParam(key) !== null) { window.history.back(); return; }
    writeHashParam(key, null, { push: false });
    setValue_(fallback);
  }, [key, fallback, ownPage]);

  return [value, set, clear];
};
